import { Box, Typography } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LocationSweepstakeDetailsHeader } from '../LocationSweepstakeDetailsHeader'
import { useParams } from 'react-router-dom'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { isNil } from 'lodash'
import { formatFullDate } from 'utils/util'
import { formatPhoneNumber } from '@jjvgaming/player-payback-library'
import { DataTable } from 'components/Shared/DataTable'
import noPatronsCheckedIn from 'assets/NoPatronsCheckedIn.png'
import { type GridRenderCellParams, type GridColDef } from '@mui/x-data-grid'
import { useEffect } from 'react'
import { Colors } from 'components/Theme'
import { colors } from 'components/ThemeProvider'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import {
  type SweepstakeWinnerStatusType,
  type SweepstakeDrawing,
} from 'src/types/api'
import { useGetSweepstakeByIdWithGroupDrawingInfo } from 'hooks/api/useGetSweepstakeByIdWithGroupDrawingInfo'
import { useSetMe } from 'stores/useMeQuery'
import { LocationPrizeMenu } from './LocationPrizeMenu'

export const LocationSweepstakesWinnersPage = () => {
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const selectedCorporateAccount = useCurrentCorporateAccountStore()
  const { id: idParam } = useParams()

  useEffect(() => {
    if (idParam) {
      void sweepstakeQuery.refetch()
    }
  }, [idParam])

  const sweepstakeQuery = useGetSweepstakeByIdWithGroupDrawingInfo({
    sweepstakeId: Number(idParam),
  })

  const getLocationWinners = (drawings: SweepstakeDrawing[]) => {
    if (!sweepstakeQuery.data || !meQuery.data || !selectedCorporateAccount) {
      return []
    }

    const allowedEstablishments = new Set(
      meQuery.data?.licensedEstablishments?.map((le) => le.id)
    )
    const allLocationDrawings =
      drawings?.filter(
        (d) =>
          d.licensedEstablishmentId === null ||
          (d.licensedEstablishment?.id !== undefined &&
            allowedEstablishments.has(d.licensedEstablishment?.id) &&
            d.licensedEstablishment?.corporateAccountId ===
              selectedCorporateAccount.currentCorporateAccountId)
      ) ?? []

    return allLocationDrawings
      .flatMap((ld) => {
        const currentSweepstake = sweepstakeQuery.data?.find(
          (x) => x.id === ld.contestId
        )
        const contestWinnersById = new Map(
          (currentSweepstake?.contestWinners ?? []).map((cw) => [cw.id, cw])
        )
        const realWinners =
          ld.sweepstakeWinners && ld.sweepstakeWinners?.length > 0
            ? ld.sweepstakeWinners.map((winner) => {
                const contestWinner = winner.contestWinnerId
                  ? contestWinnersById.get(winner.contestWinnerId)
                  : undefined
                const fullName = `${String(winner.patron?.firstName)} ${String(
                  winner.patron?.lastName
                )}`
                const validWinnerName = fullName.trim() !== ''
                const fallbackLicenseNumber =
                  winner?.contestEntry?.patronCheckIn?.licensedEstablishment
                    ?.licenseNumber
                let leLicenseNumber = '???'
                if (ld?.licensedEstablishment?.licenseNumber) {
                  leLicenseNumber = `#${String(
                    ld.licensedEstablishment.licenseNumber
                  )}`
                } else if (fallbackLicenseNumber) {
                  leLicenseNumber = `#${String(fallbackLicenseNumber)}`
                }

                return {
                  id: winner.id,
                  leName:
                    ld?.licensedEstablishment?.standardName ??
                    winner?.contestEntry?.patronCheckIn?.licensedEstablishment
                      ?.standardName ??
                    '???',
                  leLicenseNumber,
                  winnerName: validWinnerName ? fullName : '-',
                  phoneNumber: winner.patron?.phoneNumber ?? '-',
                  patronId: winner.patronId,
                  winnerStatus: winner.status ?? 'NoWinner',
                  dto: winner,
                  resultType: winner.resultType,
                  drawingSequence: winner.drawingSequence,
                  winnerNumber: contestWinner?.winnerNumber,
                  drawnDate: winner.dateDrawn ?? '-',
                  notes: winner.notes ?? '',
                  prize:
                    winner.resultType === 'Winner'
                      ? contestWinner?.prizeDescription ?? ''
                      : '',
                  isCashPrize:
                    winner.resultType === 'Winner'
                      ? contestWinner?.cashPrize ?? false
                      : false,
                }
              })
            : []

        // check if not enough winners for each location and add placeholder 'No Winner' objects
        const placeholdersNeeded =
          Number(sweepstake.numLocationWinners) -
          realWinners.filter((x) => x.resultType === 'Winner').length

        const placeholderWinners = Array.from(
          { length: Math.max(0, placeholdersNeeded) },
          (_, i) => ({
            id: Number(ld?.licensedEstablishmentId) * 100 + i, // make sure id is unique
            leName: ld?.licensedEstablishment?.standardName ?? '???',
            leLicenseNumber: ld?.licensedEstablishment?.licenseNumber
              ? `#${String(ld.licensedEstablishment.licenseNumber)}`
              : '???',
            winnerName: '-',
            patronId: -1,
            dto: {
              id: -1,
              sweepstakeDrawingId: -1,
              contestEntryId: -1,
              patronId: -1,
              patronTierId: -1,
            },
            winnerStatus: 'NoWinner',
            resultType: 'NoWinner',
            drawingSequence: Number(sweepstake.numLocationWinners) + i, // put at end of the list
            winnerNumber: undefined,
            drawnDate: '-',
            notes: '',
            prize: '',
            isCashPrize: false,
          })
        )

        return [...realWinners, ...placeholderWinners]
      })
      .filter(
        (winner) =>
          winner.resultType === 'Winner' || winner.winnerStatus === 'NoWinner'
      )
      .sort((a, b) => {
        const aSortNumber = Number(
          a.winnerNumber ?? a.drawingSequence ?? Number.MAX_SAFE_INTEGER
        )
        const bSortNumber = Number(
          b.winnerNumber ?? b.drawingSequence ?? Number.MAX_SAFE_INTEGER
        )

        // if CA or Org sweepstake, just sort by drawing sequence
        if (
          sweepstake.corporateAccountId !== null ||
          sweepstake.organizationId !== null
        ) {
          return aSortNumber - bSortNumber
          // otherwise, sort by LE name and then winnerNumber if available
        } else {
          // 1. Sort by leName alphabetically
          const leCompare = a.leName.localeCompare(b.leName)
          if (leCompare !== 0) return leCompare

          // 2. If leName is the same, sort by winnerNumber, then fall back to drawingSequence
          return aSortNumber - bSortNumber
        }
      })
  }

  const statusTextMap: Record<SweepstakeWinnerStatusType, string> = {
    LocationValidation: 'LOCATION VALIDATION',
    RedrawRequest: 'REDRAW REQUESTED',
    WinnerValidation: 'WINNER VALIDATED',
    WinnerNotified: 'WINNER NOTIFIED',
    AffidavitReceived: 'AFFIDAVIT RECEIVED',
    SentToAP: 'SENT TO AP',
    Awarded: 'AWARDED',
    NoWinner: 'NO WINNER',
    GrandPrizeValidation: 'Grand Prize Validation',
  }

  const getStatusStyleColors = (winnerStatus: SweepstakeWinnerStatusType) => {
    const background = Colors.sweepstakesWinnerStatus.background[winnerStatus]
    const color = Colors.sweepstakesWinnerStatus.text[winnerStatus]

    return {
      background,
      color,
    }
  }

  const locationWinnersColumns: GridColDef[] = [
    {
      field: 'actions',
      minWidth: 50,
      headerName: '',
      renderCell: (params: GridRenderCellParams) => {
        const hasLocationValidationWinner =
          params.row.winnerName && params.row.winnerName !== '-'
        if (hasLocationValidationWinner) {
          return (
            <LocationPrizeMenu
              params={params}
              sweepstakeName={sweepstake.name ?? ''}
            />
          )
        }
      },
    },
    {
      field: 'leName',
      minWidth: 250,
      headerName: 'Winning Entry Location',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography color={colors.text.primary}>{params.value}</Typography>
      ),
    },
    {
      field: 'leLicenseNumber',
      minWidth: 200,
      headerName: 'License Number',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography color={colors.text.primary}>{params.value}</Typography>
      ),
    },
    {
      field: 'winnerName',
      minWidth: 200,
      headerName: 'Winner Name',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography color={colors.text.primary}>{params.value}</Typography>
      ),
    },
    {
      field: 'prize',
      minWidth: 150,
      headerName: 'Prize',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography color={colors.text.primary}>{params.value}</Typography>
      ),
    },
    {
      field: 'phoneNumber',
      minWidth: 100,
      headerName: 'Phone Number',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography color={colors.text.primary}>
          {formatPhoneNumber(params.value)}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Winner Status',
      minWidth: 250,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const winnerStatus = params.row
          .winnerStatus as SweepstakeWinnerStatusType
        const statusText =
          params.row.winnerName === '-'
            ? 'NO WINNER'
            : statusTextMap[winnerStatus]
        const style = getStatusStyleColors(winnerStatus)
        return (
          <Box
            className="px-2 pt-1 pb-0.5 rounded-sm font-bold uppercase"
            sx={{ ...style }}
          >
            {statusText}
          </Box>
        )
      },
    },
    {
      field: 'drawnDate',
      minWidth: 100,
      headerName: 'Date Drawn',
      flex: 1,
      renderCell: (params) => (
        <Typography color={colors.text.primary}>
          {params.value === '-' ? params.value : formatFullDate(params.value)}
        </Typography>
      ),
    },
  ]

  if (sweepstakeQuery.isPending) {
    return <ActivityIndicator />
  }

  if (sweepstakeQuery.isError || isNil(sweepstakeQuery.data)) {
    return <Box>An error occurred fetching the sweepstake.</Box>
  }

  const sweepstake = sweepstakeQuery.data[0]

  return (
    <LocationSweepstakeDetailsHeader
      sweepstake={sweepstake}
      currentTab={`/LocationSweepstakes/${sweepstake.id}/Winners`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: 4,
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 3,
            }}
          >
            <Typography variant="h2">My Location Winners</Typography>
          </Box>
          {sweepstakeQuery.isFetching ? (
            <ActivityIndicator />
          ) : (
            sweepstakeQuery.data?.map((drawing, index) => (
              <Accordion key={drawing.id} defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h4">
                    {`Drawing ${Number(index) + 1} - ${new Date(
                      String(drawing.startDate)
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })} - ${new Date(
                      String(drawing.endDate)
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DataTable
                    columns={locationWinnersColumns}
                    forceDesktopTableInMobile
                    rows={getLocationWinners(drawing.sweepstakeDrawings ?? [])}
                    imageSource={noPatronsCheckedIn}
                    noDataMessage="After the sweepstake ends and winners are drawn, location winners will be posted here."
                  />
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Box>
    </LocationSweepstakeDetailsHeader>
  )
}
